import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { RequestStatus } from 'core/domain/Request'

import sentry from 'legacy/utils/sentry'

import { type Proposal } from '../../domain/Proposal'
import {
  confirmAwardedLanes,
  type ConfirmAwardedLanesParams,
} from '../../use-cases/confirmAwardedLanes'
import { getProposal } from '../../use-cases/getProposal'
import { submitAwardConfirmation as submitAwardConfirmationUseCase } from '../../use-cases/submitAwardConfirmation'
import { toggleAwardAutoSubmit as toggleAwardAutoSubmitUseCase } from '../../use-cases/toggleAwardAutoSubmit'

type State = {
  awardedProposal?: Proposal
  awardedProposalStatus: RequestStatus
  confirmAllLanesStatus: RequestStatus
  confirmSingleLaneStatus: RequestStatus
  submitAwardConfirmationStatus: RequestStatus
  awardAutoSubmitStatus: RequestStatus
  isAwardAutoSubmitChecked: boolean
  error?: unknown
}

const initialState = {
  awardedProposalStatus: RequestStatus.Initial,
  confirmAllLanesStatus: RequestStatus.Initial,
  confirmSingleLaneStatus: RequestStatus.Initial,
  submitAwardConfirmationStatus: RequestStatus.Initial,
  awardAutoSubmitStatus: RequestStatus.Initial,
  isAwardAutoSubmitChecked: false,
}

export function useAwardedProposal() {
  const [
    {
      awardedProposal,
      awardedProposalStatus,
      confirmAllLanesStatus,
      confirmSingleLaneStatus,
      submitAwardConfirmationStatus,
      awardAutoSubmitStatus,
      isAwardAutoSubmitChecked,
      error,
    },
    setState,
  ] = useState<State>(initialState)

  async function fetchProposal(rfpId: string, config?: AbortController) {
    return getProposal(rfpId, 'awarded=true&award_confirmation=true', config)
  }

  const fetchAwardedProposal = useCallback(async (rfpId: string, config?: AbortController) => {
    try {
      setState(previous => ({ ...previous, awardedProposalStatus: RequestStatus.Pending }))

      const awardedProposalResult = await fetchProposal(rfpId, config)

      setState(previous => ({
        ...previous,
        awardedProposal: awardedProposalResult,
        awardedProposalStatus: RequestStatus.Done,
        isAwardAutoSubmitChecked: awardedProposalResult.awardConfirmationAutoSubmit,
        error: undefined,
      }))
    } catch (error) {
      if (!config?.signal.aborted) {
        setState(previous => ({
          ...previous,
          awardedProposal: undefined,
          awardedProposalStatus: RequestStatus.Error,
          error,
        }))
      }
    }
  }, [])

  const refetchAwardedProposal = useCallback(async (rfpId: string) => {
    try {
      const awardedProposalResult = await fetchProposal(rfpId)

      setState(previous => ({
        ...previous,
        awardedProposal: awardedProposalResult,
      }))
    } catch (error) {
      sentry.log(error)
      toast.error('Error while fetching award. Please try again.')
    }
  }, [])

  const confirmAllLanes = useCallback(async (params: ConfirmAwardedLanesParams) => {
    try {
      setState(previous => ({ ...previous, confirmAllLanesStatus: RequestStatus.Pending }))

      await confirmAwardedLanes(params)

      setState(previous => ({
        ...previous,
        confirmAllLanesStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setState(previous => ({
        ...previous,
        confirmAllLanesStatus: RequestStatus.Error,
      }))

      sentry.log(error)
      toast.error('Error while confirming lanes. Please try again.')

      throw error
    }
  }, [])

  const confirmSingleLane = useCallback(async (params: ConfirmAwardedLanesParams) => {
    try {
      setState(previous => ({ ...previous, confirmSingleLaneStatus: RequestStatus.Pending }))

      await confirmAwardedLanes(params)

      setState(previous => ({
        ...previous,
        confirmSingleLaneStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setState(previous => ({
        ...previous,
        confirmSingleLaneStatus: RequestStatus.Error,
      }))

      sentry.log(error)
      toast.error('Error while confirming lane. Please try again.')

      throw error
    }
  }, [])

  const submitAwardConfirmation = useCallback(async (rfpId: string) => {
    try {
      setState(previous => ({ ...previous, submitAwardConfirmationStatus: RequestStatus.Pending }))

      await submitAwardConfirmationUseCase(rfpId)

      setState(previous => ({
        ...previous,
        submitAwardConfirmationStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setState(previous => ({
        ...previous,
        submitAwardConfirmationStatus: RequestStatus.Error,
      }))

      sentry.log(error)
      toast.error('Error while submitting award confirmation. Please try again.')

      throw error
    }
  }, [])

  const toggleAwardAutoSubmit = useCallback(
    async (rfpId: string) => {
      try {
        setState(previous => ({ ...previous, awardAutoSubmitStatus: RequestStatus.Pending }))

        const { awardConfirmationAutoSubmit } = await toggleAwardAutoSubmitUseCase(
          rfpId,
          !isAwardAutoSubmitChecked
        )

        setState(previous => ({
          ...previous,
          awardAutoSubmitStatus: RequestStatus.Done,
          isAwardAutoSubmitChecked: awardConfirmationAutoSubmit,
        }))
      } catch (error) {
        setState(previous => ({
          ...previous,
          awardAutoSubmitStatus: RequestStatus.Error,
        }))

        sentry.log(error)
        toast.error('Error while toggling award auto submit confirmation. Please try again.')

        throw error
      }
    },
    [isAwardAutoSubmitChecked]
  )

  const isConfirmingAllLanes = confirmAllLanesStatus === RequestStatus.Pending
  const isConfirmingSingleLane = confirmSingleLaneStatus === RequestStatus.Pending
  const isSubmittingAwardConfirmation = submitAwardConfirmationStatus === RequestStatus.Pending
  const isAutoSubmittingAward = awardAutoSubmitStatus === RequestStatus.Pending

  return {
    awardedProposal,
    awardedProposalStatus,
    error,
    isConfirmingAllLanes,
    isConfirmingSingleLane,
    isSubmittingAwardConfirmation,
    isAwardAutoSubmitChecked,
    isAutoSubmittingAward,
    confirmAllLanes,
    confirmSingleLane,
    fetchAwardedProposal,
    refetchAwardedProposal,
    submitAwardConfirmation,
    toggleAwardAutoSubmit,
  }
}
